/**
 * Bubble Chat Widget — Entry point.
 * Loaded globally on all Frappe desk pages via app_include_js.
 * Creates a Shadow DOM host to isolate widget CSS from Frappe desk.
 */
import cssText from "./styles.css?raw";
import { apiGet, setCsrfToken } from "./api.js";
import { BubbleChatWidget } from "./widget.js";

(function () {
	"use strict";

	// Skip on the AI Chat SPA pages — the full app handles chat there
	if (
		window.location.pathname.startsWith("/ai-chat") ||
		window.location.pathname.startsWith("/ai-meeting")
	) {
		return;
	}

	async function initBubble() {
		// Prevent double initialization
		if (document.getElementById("frappeai-bubble-host")) {
			return;
		}

		const isDesk = window.location.pathname.startsWith("/app");

		// Load settings via GET (no CSRF needed, no window.frappe needed — works
		// on Frappe Wiki pages that don't load the Frappe web bundle). The
		// server decides is_guest from the real session cookie, which is
		// reliable even on cached website pages where client-side
		// frappe.session.user would read "Guest".
		let result = {};
		try {
			result = (await apiGet("frappeai.api.get_bubble_chat_settings")) || {};
		} catch (e) {
			console.warn("[BubbleChat] Could not load settings:", e);
			return;
		}
		// Capture the session CSRF token for subsequent POST calls (chat /
		// ask_wiki_public), since the wiki page has no window.frappe.csrf_token.
		if (result.csrf_token) setCsrfToken(result.csrf_token);

		const isGuest = !!result.is_guest;

		// Desk has no guests; bail early there.
		if (isDesk && isGuest) return;

		// Decide whether to show + behaviour, based on context:
		//  - Desk page  → general AI bubble (enable_bubble_chat), session-based.
		//  - Website page (Frappe Wiki / MantisTechWeb) → Wiki Q&A bubble,
		//    always stateless (ask_wiki_public). Guests need enable+publish;
		//    authenticated users only need enable.
		let active, title, logo, guestMode = false;
		if (isDesk) {
			active = !!result.enabled;
			title = result.title || "AI Chat";
			logo = result.logo || null;
		} else {
			active = isGuest
				? !!(result.wiki_enabled && result.wiki_published)
				: !!result.wiki_enabled;
			title = result.wiki_title || "Hỏi đáp Wiki";
			logo = result.logo || null;
			guestMode = true; // website bubble is always stateless
		}

		if (!active) return;

		const settings = { enabled: true, logo, title, guestMode };

		// Create Shadow DOM host — full-viewport overlay, pointer-events:none
		// so it doesn't block clicks on the page. Child elements (bubble, panel)
		// explicitly set pointer-events:auto to receive clicks.
		const host = document.createElement("div");
		host.id = "frappeai-bubble-host";
		host.style.cssText = "position:fixed;top:0;left:0;width:100vw;height:100vh;z-index:99998;pointer-events:none;";

		const shadow = host.attachShadow({ mode: "open" });

		// Inject styles
		const style = document.createElement("style");
		style.textContent = cssText;
		shadow.appendChild(style);

		document.body.appendChild(host);

		// Initialize widget with pre-loaded settings
		new BubbleChatWidget(shadow, settings);
	}

	// Wait for DOM and Frappe to be ready
	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", function () {
			setTimeout(initBubble, 500);
		});
	} else {
		setTimeout(initBubble, 500);
	}
})();
